import type { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useMemo, useState, useRef } from 'preact/hooks';
import { useRoute } from 'preact-iso';
import { useRequest, Dialog, ErrorNotification } from '@ox/blox';
import { visitFormsApi } from '@ox/visits-ui/clients/mtx';
import type { MediaFile } from '@ox/ox-brokerage-client';

/** Format seconds as M:SS or H:MM:SS */
const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
};

/** Shared button style for top-bar and bottom-bar controls */
const controlBtnStyle: preact.JSX.CSSProperties = {
  padding: '0.5rem 0.75rem',
  backgroundColor: 'rgba(255,255,255,0.2)',
  color: 'white',
  border: '1px solid rgba(255,255,255,0.3)',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
  lineHeight: '1',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * SVG icon helpers – kept tiny so we avoid external icon deps.
 * viewBox is always "0 0 24 24", fill="currentColor".
 */
const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);
const PauseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);
const VolumeUpIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);
const VolumeMuteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);
const FullscreenIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
  </svg>
);
const RotateIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.11 8.53L5.7 7.11C4.8 8.27 4.24 9.61 4.07 11h2.02c.14-.87.49-1.72 1.02-2.47zM6.09 13H4.07c.17 1.39.72 2.73 1.62 3.89l1.41-1.42c-.52-.75-.87-1.59-1.01-2.47zm1.01 5.32c1.16.9 2.51 1.44 3.9 1.61V17.9c-.87-.15-1.71-.49-2.46-1.03L7.1 18.32zM13 4.07V1L8.45 5.55 13 10V6.09c2.84.48 5 2.94 5 5.91s-2.16 5.43-5 5.91v2.02c3.95-.49 7-3.85 7-7.93s-3.05-7.44-7-7.93z" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
  </svg>
);

const PLAYBACK_RATES = [1, 1.5, 2, 3, 4] as const;

type RotationDeg = 0 | 90 | 180 | 270;

/**
 * Video component
 */
export const VideoPage: FunctionalComponent = () => {
  const { params } = useRoute();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptText, setTranscriptText] = useState<string | null>(null);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);

  // Video playback state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [rotation, setRotation] = useState<RotationDeg>(0);
  const [showControls, setShowControls] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const hideControlsTimer = useRef<number | null>(null);

  const { isPending, results, request } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.getWalkthroughVideoSignedUrlsApiV1VisitFormsVideoSignedUrlsVisitFormIdGet,
  });

  useEffect(() => {
    request({ visitFormId: params.visitFormId as string });
  }, [request, params.visitFormId]);

  // signedUrl is typed as SignedUrl (empty interface) but is actually a string at runtime
  const videos = results.data || [];
  const signedUrls = videos.map((video) => video.signedUrl as unknown as string);
  const currentUrl = signedUrls[currentIndex];
  const totalVideos = signedUrls.length;
  const currentVideo: MediaFile | undefined = videos[currentIndex];
  const currentNote = currentVideo?.note as unknown as string | null | undefined;

  // Reset rotation and playback rate when switching videos
  useEffect(() => {
    setRotation(0);
    setPlaybackRate(1);
  }, [currentIndex]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (hideControlsTimer.current) {
        clearTimeout(hideControlsTimer.current);
      }
    };
  }, []);

  // ---------- Custom video-control helpers ----------

  const scheduleHideControls = useCallback(() => {
    if (hideControlsTimer.current) {
      clearTimeout(hideControlsTimer.current);
    }
    hideControlsTimer.current = window.setTimeout(() => {
      // Only auto-hide while playing
      if (videoRef.current && !videoRef.current.paused) {
        setShowControls(false);
      }
    }, 3000);
  }, []);

  const revealControls = useCallback(() => {
    setShowControls(true);
    scheduleHideControls();
  }, [scheduleHideControls]);

  // Sync play/pause state from the <video> element
  useEffect(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    const onPlay = () => {
      setIsPlaying(true);
      scheduleHideControls();
    };
    const onPause = () => {
      setIsPlaying(false);
      setShowControls(true);
    };
    const onTime = () => setCurrentTime(v.currentTime);
    const onDur = () => setDuration(v.duration);
    const onVol = () => {
      setVolume(v.volume);
      setIsMuted(v.muted);
    };
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onDur);
    v.addEventListener('durationchange', onDur);
    v.addEventListener('volumechange', onVol);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onDur);
      v.removeEventListener('durationchange', onDur);
      v.removeEventListener('volumechange', onVol);
    };
  }, [currentUrl, scheduleHideControls]); // re-attach when video source changes

  // Sync playback rate to video element
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.playbackRate = playbackRate;
    }
  }, [playbackRate, currentUrl]);

  const cyclePlaybackRate = useCallback(() => {
    const PLAYBACK_RATES = [1, 1.5, 2, 3, 4] as const;
    setPlaybackRate((prev) => {
      const idx = PLAYBACK_RATES.indexOf(prev as (typeof PLAYBACK_RATES)[number]);
      return PLAYBACK_RATES[(idx + 1) % PLAYBACK_RATES.length];
    });
  }, []);

  const decreasePlaybackRate = useCallback(() => {
    const PLAYBACK_RATES = [1, 1.5, 2, 3, 4] as const;
    setPlaybackRate((prev) => {
      const idx = PLAYBACK_RATES.indexOf(prev as (typeof PLAYBACK_RATES)[number]);
      return PLAYBACK_RATES[(idx - 1 + PLAYBACK_RATES.length) % PLAYBACK_RATES.length];
    });
  }, []);

  const seekForward = useCallback(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    v.currentTime = Math.min(v.currentTime + 5, v.duration || 0);
  }, []);

  const seekBackward = useCallback(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    v.currentTime = Math.max(v.currentTime - 5, 0);
  }, []);

  const togglePlayPause = useCallback(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    if (v.paused) {
      v.play();
    } else {
      v.pause();
    }
  }, []);

  const handleSeek = useCallback((e: Event) => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    v.currentTime = Number((e.target as HTMLInputElement).value);
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    v.muted = !v.muted;
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = videoRef.current?.parentElement?.parentElement; // .video-page root div
    if (!container) {
      return;
    }
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen?.();
    }
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((prev) => ((prev + 90) % 360) as RotationDeg);
  }, []);

  // Download video using dedicated download endpoint
  const { request: requestDownload } = useRequest({
    context: visitFormsApi,
    method: visitFormsApi.getMediaDownloadUrlApiV1VisitFormsDownloadMediaMediaIdGet,
  });

  const handleDownload = useCallback(async () => {
    if (!currentVideo) {
      return;
    }

    setIsDownloading(true);
    try {
      const downloadData = await requestDownload({ mediaId: currentVideo.id });
      if (!downloadData.data?.signedUrl) {
        throw new Error(`No download URL returned for ${currentVideo.originalFilename}`);
      }

      // Open the signed URL directly so the browser handles the download natively
      // (shows progress in the browser's download tab)
      const link = document.createElement('a');
      link.href = downloadData.data.signedUrl as string;
      link.download = currentVideo.originalFilename || currentVideo.mediaType || 'video';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download video. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [currentVideo, requestDownload]);

  // Compute video transform style based on rotation
  const videoTransformStyle = useMemo((): preact.JSX.CSSProperties => {
    const isSideways = rotation === 90 || rotation === 270;
    const base: preact.JSX.CSSProperties = {
      width: '100%',
      height: '100%',
      display: 'block',
      objectFit: 'contain',
      transform: `rotate(${rotation}deg)${isSideways ? ' scale(calc(100vh / 100vw))' : ''}`,
      transition: 'transform 0.3s ease',
    };
    if (isSideways) {
      // When rotated sideways we need to swap aspect to fit viewport
      base.transformOrigin = 'center center';
    }
    return base;
  }, [rotation]);

  // ---------- Transcription logic (unchanged) ----------

  // Poll for transcription status
  const pollTranscriptionStatus = useCallback(
    async (mediaId: string, jobNameParam: string) => {
      try {
        const status =
          await visitFormsApi.getTranscriptionStatusApiV1VisitFormsTranscribeStatusMediaIdJobNameGet(
            {
              mediaId,
              jobName: jobNameParam,
            },
          );

        if (status.status === 'COMPLETED' && status.transcript) {
          setTranscriptText(status.transcript as unknown as string);
          setIsTranscribing(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          request({ visitFormId: params.visitFormId as string });
        } else if (status.status === 'FAILED') {
          setTranscriptionError(
            (status.failureReason as unknown as string) || 'Transcription failed',
          );
          setIsTranscribing(false);
          setShowTranscriptModal(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
        // If status is IN_PROGRESS, continue polling
      } catch (error) {
        console.error('Error polling transcription status:', error);
        setTranscriptionError('Failed to check transcription status');
        setIsTranscribing(false);
        setShowTranscriptModal(false);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    },
    [request, params.visitFormId],
  );

  // Start transcription
  const startTranscription = useCallback(async () => {
    if (!currentVideo?.id) {
      return;
    }

    setIsTranscribing(true);
    setTranscriptionError(null);
    setShowTranscriptModal(true);

    try {
      const response = await visitFormsApi.startTranscriptionApiV1VisitFormsTranscribeStartPost({
        startTranscriptionRequest: { mediaId: currentVideo.id },
      });

      // Start polling every 5 seconds
      pollingIntervalRef.current = window.setInterval(() => {
        pollTranscriptionStatus(currentVideo.id, response.jobName);
      }, 5000);

      // Also poll immediately
      pollTranscriptionStatus(currentVideo.id, response.jobName);
    } catch (error) {
      console.error('Error starting transcription:', error);
      setTranscriptionError('Failed to start transcription');
      setIsTranscribing(false);
    }
  }, [currentVideo, pollTranscriptionStatus]);

  // Handle transcribe button click
  const handleTranscribeClick = useCallback(() => {
    if (!currentNote || currentNote.startsWith('transcription-')) {
      // Start transcription
      startTranscription();
    } else {
      // Show existing note
      setTranscriptText(currentNote);
      setShowTranscriptModal(true);
    }
  }, [currentNote, startTranscription]);

  // Define handlers before any conditional returns (hooks must be called unconditionally)
  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalVideos - 1));
  }, [totalVideos]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < totalVideos - 1 ? prev + 1 : 0));
  }, [totalVideos]);

  const handleCloseModal = useCallback(() => {
    setShowTranscriptModal(false);
    // Don't clear transcript or error immediately - let them persist for next open
  }, []);

  const handleDismissError = useCallback(() => {
    setTranscriptionError(null);
  }, []);

  const handleControlBarClick = useCallback((e: Event) => {
    e.stopPropagation();
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Don't fire when the user is typing in an input or textarea
      const tag = (e.target as HTMLElement)?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowRight':
          e.preventDefault();
          seekForward();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seekBackward();
          break;
        case 'ArrowUp':
          e.preventDefault();
          cyclePlaybackRate();
          break;
        case 'ArrowDown':
          e.preventDefault();
          decreasePlaybackRate();
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          handleRotate();
          break;
        case 't':
        case 'T':
          e.preventDefault();
          handleTranscribeClick();
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    togglePlayPause,
    seekForward,
    seekBackward,
    cyclePlaybackRate,
    decreasePlaybackRate,
    handleRotate,
    handleTranscribeClick,
  ]);

  // Show loading state while fetching URL
  if (isPending) {
    return (
      <div className="image-gallery__loader">
        <div className="image-gallery__spinner" />
      </div>
    );
  }

  // Show error if request failed
  if (results.error) {
    alert('Error loading video. Please try again later.');
    window.close();
    return null;
  }

  // No videos found
  if (totalVideos === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No videos found</p>
      </div>
    );
  }

  const seekPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      style={{ position: 'relative', height: '100vh', backgroundColor: '#000', overflow: 'hidden' }}
      onMouseMove={revealControls}
      onTouchStart={revealControls}
    >
      <ErrorNotification open={!!transcriptionError} icon="info" onDismiss={handleDismissError}>
        {transcriptionError}
      </ErrorNotification>

      {/* ===== Top bar (transcribe + nav) ===== */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)',
          zIndex: 10,
          gap: '0.5rem',
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: showControls ? 'auto' : 'none',
        }}
      >
        {/* Transcribe button on the left */}
        <button
          onClick={handleTranscribeClick}
          style={{ ...controlBtnStyle, minWidth: '90px' }}
          aria-label="Transcribe video"
          title="Transcribe (T)"
        >
          Transcribe
        </button>

        {/* Navigation controls on the right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span
            style={{ color: 'white', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap' }}
          >
            {currentIndex + 1} / {totalVideos}
          </span>
          <button
            onClick={handlePrevious}
            style={{ ...controlBtnStyle, minWidth: '60px' }}
            aria-label="Previous video"
          >
            ← Prev
          </button>
          <button
            onClick={handleNext}
            style={{ ...controlBtnStyle, minWidth: '60px' }}
            aria-label="Next video"
          >
            Next →
          </button>
        </div>
      </div>

      {/* ===== Video (click to play/pause) ===== */}
      <div style={{ width: '100%', height: '100%', overflow: 'hidden' }} onClick={togglePlayPause}>
        <video
          ref={videoRef}
          key={currentUrl}
          autoPlay={false}
          playsInline
          style={videoTransformStyle}
        >
          <source src={currentUrl} type="video/quicktime" />
          <source src={currentUrl} type="video/mp4" />
          <track kind="captions" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* ===== Bottom custom controls bar ===== */}
      <div
        role="toolbar"
        onClick={handleControlBarClick}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.35rem',
          padding: '0.5rem 0.75rem',
          background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
          zIndex: 10,
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: showControls ? 'auto' : 'none',
        }}
      >
        {/* Row 1: Seek bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ color: 'white', fontSize: '11px', whiteSpace: 'nowrap' }}>
            {formatTime(currentTime)}
          </span>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={currentTime}
            onInput={handleSeek}
            aria-label="Seek"
            title="Seek (← −5s  /  → +5s)"
            style={{
              flex: 1,
              height: '4px',
              cursor: 'pointer',
              accentColor: '#3498db',
              background: `linear-gradient(to right, #3498db ${seekPercent}%, rgba(255,255,255,0.3) ${seekPercent}%)`,
              borderRadius: '2px',
              WebkitAppearance: 'none',
              appearance: 'none',
            }}
          />
          <span style={{ color: 'white', fontSize: '11px', whiteSpace: 'nowrap' }}>
            {formatTime(duration)}
          </span>
        </div>

        {/* Row 2: Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Left group: play + volume */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <button
              onClick={togglePlayPause}
              style={{ ...controlBtnStyle, padding: '0.4rem', minWidth: 'auto' }}
              aria-label={isPlaying ? 'Pause' : 'Play'}
              title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
            <button
              onClick={toggleMute}
              style={{ ...controlBtnStyle, padding: '0.4rem', minWidth: 'auto' }}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted || volume === 0 ? <VolumeMuteIcon /> : <VolumeUpIcon />}
            </button>
          </div>

          {/* Right group: rotate, download, fullscreen */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <button
              onClick={cyclePlaybackRate}
              style={{
                ...controlBtnStyle,
                padding: '0.4rem',
                minWidth: '40px',
                fontWeight: playbackRate !== 1 ? 700 : 400,
              }}
              aria-label={`Playback speed ${playbackRate}x`}
              title={`Speed: ${playbackRate}x (↑ faster / ↓ slower)`}
            >
              {playbackRate}x
            </button>
            <button
              onClick={handleRotate}
              style={{ ...controlBtnStyle, padding: '0.4rem', minWidth: 'auto' }}
              aria-label="Rotate video 90°"
              title="Rotate 90° (R)"
            >
              <RotateIcon />
            </button>
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              style={{
                ...controlBtnStyle,
                padding: '0.4rem',
                minWidth: 'auto',
                opacity: isDownloading ? 0.5 : 1,
              }}
              aria-label="Download video"
              title="Download"
            >
              <DownloadIcon />
            </button>
            <button
              onClick={toggleFullscreen}
              style={{ ...controlBtnStyle, padding: '0.4rem', minWidth: 'auto' }}
              aria-label="Toggle fullscreen"
              title="Fullscreen"
            >
              <FullscreenIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Transcription Modal */}
      {showTranscriptModal && (
        <Dialog
          heading="Video Transcription"
          open={showTranscriptModal}
          onDismiss={handleCloseModal}
          type="modal"
        >
          <div style={{ minHeight: '200px', maxHeight: '60vh', overflow: 'auto' }}>
            {isTranscribing ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '2rem',
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <div style={{ marginTop: '1rem', color: '#666', textAlign: 'center' }}>
                  Transcribing video...
                  <br />
                  It will take a few minutes.
                </div>
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </div>
            ) : transcriptText ? (
              <div style={{ padding: '1rem', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {transcriptText}
              </div>
            ) : (
              <div style={{ padding: '1rem', color: '#666' }}>
                <p>No transcription available.</p>
              </div>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
};
